import {
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Can } from '@gbs-monorepo-packages/auth';
import {
  BaseDropdown,
  BaseTooltip,
  HighUserRoles,
  type IRole,
  NotEmployeeRoles,
  useScreenSize,
} from '@gbs-monorepo-packages/common';

import { type ICourseDTO } from '../../../../services/courses';
import {
  ActionButton,
  ActionsButtonContainer,
  AddLinkIcon,
  AllDropdownItemsContainer,
  BookmarkBorderIcon,
  BookmarkIcon,
  DeleteIcon,
  DownloadIcon,
  DropdownActionItem,
  DropdownButtonContainer,
  DuplicateIcon,
  EditIcon,
  LastDropdownItems,
  LinkIcon,
  MainButtonCustom,
  PreviewIcon,
  TriggerDropdownAction,
} from './styles';

interface IAdminAction {
  dataCy: string;
  label: string;
  onClick: MouseEventHandler<HTMLButtonElement | HTMLDivElement>;
}

type ICurrentView = 'desktop' | 'grid' | 'mobile';

interface ICourseActionsProps {
  course: ICourseDTO;
  editCoursePath: string;
  view: ICurrentView;
  canEditCourseBoolean: boolean;
  currentRole: IRole | null;
  handleClickOnCard: (editCoursePath: string, course: ICourseDTO) => void;
  courseDownload: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  handleToAdminDuplicateCourse: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  handleDuplicateCourse: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  viewCourse: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  handleOpenModalCreateViewURL: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  handleOpenModalDelete: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  handleOpenModalChangeStatus: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  handleOpenModalChangeImage: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  handleOpenModalCourseRestore: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  handleOpenModalGenerateCourseTemplateByCourse: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>,
    course: ICourseDTO
  ) => void;
  setIsDropdownActionsOpen: (isOpen: boolean) => void;
  setIsDropdownGridOpen: (isOpen: boolean) => void;
  setVisibleWhenCourseOptionsOpen: (courseId: number | null) => void;
  toggleBookmarkCourse: (courseId: number) => void;
}

const ActionItem = ({
  dataCy,
  onClick,
  children,
}: {
  dataCy?: string;
  onClick: MouseEventHandler<HTMLButtonElement | HTMLDivElement>;
  children: ReactNode;
}) => (
  <DropdownActionItem data-cy={dataCy} onClick={onClick}>
    {children}
  </DropdownActionItem>
);

const NotViewer = NotEmployeeRoles;

const getActions = (
  course: ICourseDTO,
  handlers: {
    onChangeStatus: MouseEventHandler;
    onChangeImage: MouseEventHandler;
    onRestoreCourse: MouseEventHandler;
    onGenerateTemplate: MouseEventHandler;
  }
): IAdminAction[] => [
  {
    dataCy: 'option-status',
    label: 'Status',
    onClick: handlers.onChangeStatus,
  },
  {
    dataCy: 'option-changeImage',
    label: 'Change Image',
    onClick: handlers.onChangeImage,
  },
  {
    dataCy: 'option-courseRestore',
    label: 'Course Restore',
    onClick: handlers.onRestoreCourse,
  },
  {
    dataCy: 'option-generateCourseTemplate',
    label: 'Generate Course Template',
    onClick: handlers.onGenerateTemplate,
  },
];

export const CourseActions = ({
  course,
  editCoursePath,
  view,
  canEditCourseBoolean,
  currentRole,
  handleClickOnCard,
  courseDownload,
  handleToAdminDuplicateCourse,
  handleDuplicateCourse,
  viewCourse,
  handleOpenModalCreateViewURL,
  handleOpenModalDelete,
  handleOpenModalChangeStatus,
  handleOpenModalChangeImage,
  handleOpenModalCourseRestore,
  handleOpenModalGenerateCourseTemplateByCourse,
  setIsDropdownActionsOpen,
  setIsDropdownGridOpen,
  setVisibleWhenCourseOptionsOpen,
  toggleBookmarkCourse,
}: ICourseActionsProps) => {
  const { windowSize } = useScreenSize();
  const { width } = windowSize;
  const [currentView, setCurrentView] = useState<ICurrentView>(view);

  const handleDuplicate = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    if (currentRole?.level && currentRole?.level > 3) {
      handleToAdminDuplicateCourse(e, course);
    } else {
      void handleDuplicateCourse(e, course);
    }
  };

  const adminActions = useMemo(
    () =>
      getActions(course, {
        onChangeStatus: (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) =>
          handleOpenModalChangeStatus(e, course),
        onChangeImage: (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) =>
          handleOpenModalChangeImage(e, course),
        onRestoreCourse: (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) =>
          handleOpenModalCourseRestore(e, course),
        onGenerateTemplate: (
          e: MouseEvent<HTMLButtonElement | HTMLDivElement>
        ) => handleOpenModalGenerateCourseTemplateByCourse(e, course),
      }),
    [
      course,
      handleOpenModalChangeStatus,
      handleOpenModalChangeImage,
      handleOpenModalCourseRestore,
      handleOpenModalGenerateCourseTemplateByCourse,
    ]
  );

  const handleBookmark = (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement>
  ) => {
    e.stopPropagation();
    void toggleBookmarkCourse(course.id);
  };

  const lastDropdownItems = (
    <LastDropdownItems $view={currentView}>
      {adminActions.map(({ dataCy, label, onClick }) => (
        <ActionItem key={dataCy} dataCy={dataCy} onClick={onClick}>
          {label}
        </ActionItem>
      ))}
    </LastDropdownItems>
  );

  const allDropdownItems = (
    <AllDropdownItemsContainer $view={currentView}>
      <Can roles={NotViewer}>
        <ActionItem
          dataCy="option-edit"
          onClick={() => handleClickOnCard(editCoursePath, course)}
        >
          Edit
        </ActionItem>
      </Can>

      <ActionItem
        dataCy="option-download"
        onClick={(e) => courseDownload(e, course)}
      >
        Download
      </ActionItem>

      <Can roles={HighUserRoles}>
        <ActionItem dataCy="option-duplicate" onClick={handleDuplicate}>
          Duplicate
        </ActionItem>
      </Can>

      {(canEditCourseBoolean || course.viewURL) && (
        <>
          <ActionItem
            dataCy="option-preview"
            onClick={(e) => viewCourse(e, course)}
          >
            Preview
          </ActionItem>

          <ActionItem
            dataCy="option-addLink"
            onClick={(e) => handleOpenModalCreateViewURL(e, course)}
          >
            {course.viewURL ? 'Copy Link' : 'Add Link'}
          </ActionItem>
        </>
      )}

      <Can roles={HighUserRoles}>
        <ActionItem
          dataCy="option-delete"
          onClick={(e) => handleOpenModalDelete(e, course)}
        >
          Delete
        </ActionItem>
        <ActionItem data-cy="button-bookmark" onClick={handleBookmark}>
          {course.bookmark ? 'Unbookmark' : 'Bookmark'}
        </ActionItem>

        {lastDropdownItems}
      </Can>
    </AllDropdownItemsContainer>
  );

  useEffect(() => {
    if (currentView === 'grid') {
      return;
    }

    setIsDropdownGridOpen(false);
    setIsDropdownActionsOpen(false);
    setVisibleWhenCourseOptionsOpen(null);

    if (width <= 1000) {
      setCurrentView('mobile');
    } else {
      setCurrentView('desktop');
    }
  }, [
    width,
    currentView,
    setIsDropdownGridOpen,
    setIsDropdownActionsOpen,
    setVisibleWhenCourseOptionsOpen,
  ]);

  if (currentView === 'desktop') {
    return (
      <ActionsButtonContainer data-cy="fast-actions-container">
        <Can roles={NotViewer}>
          <BaseTooltip message="Edit">
            <ActionButton
              data-cy="button-edit"
              onClick={() => handleClickOnCard(editCoursePath, course)}
            >
              <EditIcon />
            </ActionButton>
          </BaseTooltip>
        </Can>
        <BaseTooltip message="Download">
          <ActionButton
            data-cy="button-download"
            onClick={(e) => courseDownload(e, course)}
          >
            <DownloadIcon />
          </ActionButton>
        </BaseTooltip>
        <Can roles={HighUserRoles}>
          <BaseTooltip message="Duplicate">
            <ActionButton data-cy="button-duplicate" onClick={handleDuplicate}>
              <DuplicateIcon />
            </ActionButton>
          </BaseTooltip>
        </Can>
        {(canEditCourseBoolean || course.viewURL) && (
          <>
            <BaseTooltip message="Preview">
              <ActionButton
                data-cy="button-preview"
                onClick={(e) => viewCourse(e, course)}
              >
                <PreviewIcon />
              </ActionButton>
            </BaseTooltip>
            <BaseTooltip message={course.viewURL ? 'Copy Link' : 'Add Link'}>
              <MainButtonCustom
                styleType="text"
                dataCy="button-send"
                onClick={(e) => handleOpenModalCreateViewURL(e, course)}
              >
                {course.viewURL ? (
                  <LinkIcon data-cy="button-copyLink" />
                ) : (
                  <AddLinkIcon data-cy="button-addLink" />
                )}
              </MainButtonCustom>
            </BaseTooltip>
          </>
        )}
        <Can roles={HighUserRoles}>
          <BaseTooltip message="Delete">
            <ActionButton
              data-cy="button-delete"
              onClick={(e) => {
                handleOpenModalDelete(e, course);
              }}
            >
              <DeleteIcon />
            </ActionButton>
          </BaseTooltip>
          <BaseTooltip message={course.bookmark ? 'Unbookmark' : 'Bookmark'}>
            <ActionButton data-cy="button-bookmark" onClick={handleBookmark}>
              {course.bookmark ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            </ActionButton>
          </BaseTooltip>
          <BaseDropdown
            key={`desktop-${width}`}
            dataCy="button-anotherCourseAction"
            customTrigger={<TriggerDropdownAction />}
            onOpenChange={(open) => {
              setIsDropdownActionsOpen(open);
              setVisibleWhenCourseOptionsOpen(open ? course.id : null);
            }}
          >
            {lastDropdownItems}
          </BaseDropdown>
        </Can>
      </ActionsButtonContainer>
    );
  }

  if (currentView === 'mobile' || currentView === 'grid') {
    return (
      <DropdownButtonContainer data-cy="dropdown-container">
        <BaseDropdown
          key={`${view}-${width}`}
          dataCy={`dropdown-course`}
          onOpenChange={(isOpen) => {
            if (view === 'mobile') setIsDropdownActionsOpen(isOpen);
            if (view === 'grid') setIsDropdownGridOpen(isOpen);
          }}
          customTrigger={<TriggerDropdownAction />}
        >
          {allDropdownItems}
        </BaseDropdown>
      </DropdownButtonContainer>
    );
  }

  return null;
};
